import {
  CIRCULAR_DNA_PART_ARROW_VERTICAL_OFFSET,
  CIRCULAR_DNA_PART_ARROW_HORIZONTAL_OFFSET,
  CIRCULAR_DNA_PART_ARROW_TIP_HEIGHT,
  CIRCULAR_DNA_PART_TICK_LENGTH,
  CIRCULAR_DNA_PART_ARROW_TIP_WIDTH,
  DNA_PART_TICK_SPACING,
} from 'molbio/maps/BiointelligentLinksConstants';
import arcs from 'molbio/maps/circular/Arcs';
import {GenericArcDatum} from 'molbio/maps/circular/CircularMapTypedefs';
import {HasRadius} from 'molbio/maps/circular/CircularTypedefs';
import {arcLengthToRadians, polarToCartesian} from 'util/geometry/GeometryUtil';

const CIRCLE_CENTER = {x: 0, y: 0};

/**
 * Generates SVG path for arrow tips (triangular tips at the end of part annotations)
 * on a circular sequence map.
 *
 * @param angles - Start and end angles for the part annotation
 * @param dimensions - Inner and outer radius of the annotation ring
 * @param isRevcomp - Whether the part is on the reverse complement strand
 * @returns SVG path string for both inner and outer arrow tips
 */
export function getCircularArrowTipsPath(
  angles: GenericArcDatum,
  dimensions: HasRadius,
  isRevcomp: boolean
): string {
  const innerArrowRadius = dimensions.innerRadius + CIRCULAR_DNA_PART_ARROW_VERTICAL_OFFSET / 2;
  const partArrowPaddingInRadians = arcLengthToRadians(
    CIRCULAR_DNA_PART_ARROW_HORIZONTAL_OFFSET,
    innerArrowRadius
  );

  // Arrow tip is at the start for reverse complement, end for forward
  const arrowTipAngle = isRevcomp
    ? angles.startAngle + partArrowPaddingInRadians
    : angles.endAngle - partArrowPaddingInRadians;

  // Calculate inner arrow tip
  const innerArrowOuterPoint = polarToCartesian(
    CIRCLE_CENTER,
    innerArrowRadius + 0.5 * CIRCULAR_DNA_PART_ARROW_TIP_HEIGHT,
    arrowTipAngle
  );
  const innerArrowInnerPoint = polarToCartesian(
    CIRCLE_CENTER,
    innerArrowRadius - 0.5 * CIRCULAR_DNA_PART_ARROW_TIP_HEIGHT,
    arrowTipAngle
  );
  const arrowTipLengthInRadians = arcLengthToRadians(CIRCULAR_DNA_PART_ARROW_TIP_WIDTH, innerArrowRadius);
  const innerArrowTipPoint = polarToCartesian(
    CIRCLE_CENTER,
    innerArrowRadius,
    arrowTipAngle + (isRevcomp ? -1 : 1) * arrowTipLengthInRadians
  );
  const innerArrowTip = `M${innerArrowOuterPoint.x},${innerArrowOuterPoint.y} L${innerArrowInnerPoint.x},${innerArrowInnerPoint.y} L${innerArrowTipPoint.x},${innerArrowTipPoint.y} Z`;

  // Calculate outer arrow tip
  const outerArrowRadius = dimensions.outerRadius - CIRCULAR_DNA_PART_ARROW_VERTICAL_OFFSET / 2;
  const outerArrowOuterPoint = polarToCartesian(
    CIRCLE_CENTER,
    outerArrowRadius + 0.5 * CIRCULAR_DNA_PART_ARROW_TIP_HEIGHT,
    arrowTipAngle
  );
  const outerArrowInnerPoint = polarToCartesian(
    CIRCLE_CENTER,
    outerArrowRadius - 0.5 * CIRCULAR_DNA_PART_ARROW_TIP_HEIGHT,
    arrowTipAngle
  );
  const outerArrowTipPoint = polarToCartesian(
    CIRCLE_CENTER,
    outerArrowRadius,
    arrowTipAngle + (isRevcomp ? -1 : 1) * arrowTipLengthInRadians
  );
  const outerArrowTip = `M${outerArrowOuterPoint.x},${outerArrowOuterPoint.y} L${outerArrowInnerPoint.x},${outerArrowInnerPoint.y} L${outerArrowTipPoint.x},${outerArrowTipPoint.y} Z`;

  return `${innerArrowTip} ${outerArrowTip}`;
}

/**
 * Generates SVG path for the arrow body (arcs) and tick marks on a circular sequence map.
 * The ticks indicate the direction of the part.
 *
 * @param angles - Start and end angles for the part annotation
 * @param dimensions - Inner and outer radius of the annotation ring
 * @param isRevcomp - Whether the part is on the reverse complement strand
 * @returns SVG path string for arcs and tick marks
 */
export function getCircularArrowBodyAndTicksPath(
  angles: GenericArcDatum,
  dimensions: HasRadius,
  isRevcomp: boolean
): string {
  const innerArrowRadius = dimensions.innerRadius + CIRCULAR_DNA_PART_ARROW_VERTICAL_OFFSET / 2;
  const outerArrowRadius = dimensions.outerRadius - CIRCULAR_DNA_PART_ARROW_VERTICAL_OFFSET / 2;
  const partArrowPaddingInRadians = arcLengthToRadians(
    CIRCULAR_DNA_PART_ARROW_HORIZONTAL_OFFSET,
    innerArrowRadius
  );
  const arcStartAngle = angles.startAngle + partArrowPaddingInRadians;
  const arcEndAngle = angles.endAngle - partArrowPaddingInRadians;

  // Generate inner and outer arcs
  // The linearArc expects data with radius, startAngle, endAngle, and reverse properties
  const arcData = {
    radius: innerArrowRadius,
    startAngle: arcStartAngle,
    endAngle: arcEndAngle,
    reverse: false,
  };

  const innerArrowArc = arcs.linearArc();
  const innerArrow = innerArrowArc(arcData);

  const outerArcData = {
    radius: outerArrowRadius,
    startAngle: arcStartAngle,
    endAngle: arcEndAngle,
    reverse: false,
  };

  const outerArrowArc = arcs.linearArc();
  const outerArrow = outerArrowArc(outerArcData);

  // Generate tick marks
  const tickSpacing = arcLengthToRadians(DNA_PART_TICK_SPACING, innerArrowRadius);
  const innerTicks = (isRevcomp ? getReverseCircularTickLinePath : getForwardCircularTickLinePath)(
    arcStartAngle,
    arcEndAngle,
    innerArrowRadius,
    tickSpacing
  );

  const outerTicks = (isRevcomp ? getReverseCircularTickLinePath : getForwardCircularTickLinePath)(
    arcStartAngle,
    arcEndAngle,
    outerArrowRadius - CIRCULAR_DNA_PART_TICK_LENGTH,
    tickSpacing
  );

  return `${innerArrow} ${innerTicks} ${outerArrow} ${outerTicks}`;
}

function getForwardCircularTickLinePath(
  startAngle: number,
  endAngle: number,
  innerRadius: number,
  tickSpacing: number
): string {
  const allTickLines = [];
  const outerRadius = innerRadius + CIRCULAR_DNA_PART_TICK_LENGTH;
  // We want to start at the side of the arrow next to the tip, so that the first tick is placed at a
  // reasonable distance from the tip.
  let currentAngle = endAngle - tickSpacing;
  while (currentAngle > startAngle) {
    const innerPoint = polarToCartesian(CIRCLE_CENTER, innerRadius, currentAngle);
    const outerPoint = polarToCartesian(CIRCLE_CENTER, outerRadius, currentAngle);
    allTickLines.push(`M${innerPoint.x},${innerPoint.y} L${outerPoint.x},${outerPoint.y}`);
    // When drawing a forward arrow, we proceed from the tip backwards towards the start angle.
    currentAngle -= tickSpacing;
  }
  return allTickLines.join(' ');
}

function getReverseCircularTickLinePath(
  startAngle: number,
  endAngle: number,
  innerRadius: number,
  tickSpacing: number
): string {
  const allTickLines = [];
  const outerRadius = innerRadius + CIRCULAR_DNA_PART_TICK_LENGTH;
  // We want to start at the side of the arrow next to the tip, so that the first tick is placed at a
  // reasonable distance from the tip.
  let currentAngle = startAngle + tickSpacing;
  while (currentAngle < endAngle) {
    const innerPoint = polarToCartesian(CIRCLE_CENTER, innerRadius, currentAngle);
    const outerPoint = polarToCartesian(CIRCLE_CENTER, outerRadius, currentAngle);
    allTickLines.push(`M${innerPoint.x},${innerPoint.y} L${outerPoint.x},${outerPoint.y}`);
    // When drawing a reverse arrow, we proceed from the tip forwards towards the end angle.
    currentAngle += tickSpacing;
  }
  return allTickLines.join(' ');
}
